'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import { DMButton } from '@/components/button';
import DMInput from '@/components/input/TextInput';
import { useToast } from '@/components/ui/use-toast';
import { usePayment } from '@/hooks/usePayment';
import useNotificationStore from '@/lib/zustand/store/notificationStore';
import useOneOnOneStore from '@/lib/zustand/store/oneOnOneStore';
import PollingUi from '@/screens/courses/courses-details/components/razorpay/PollingUi';
import { getProfile } from '@/screens/profile/actions';
import { ErrorMessage } from '@/screens/profile/types/type';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { DebugMediaItem } from '..';
import doneLottie from '../../../../public/lottie/Done-Lottie.json';
import { formatLocalDate, formatLocalTime } from '../utils/helpers';

const bookingSchema = z.object({
  name: z.string().min(3, { message: 'Name must be at least 3 characters' }),
  email: z.string().email({ message: 'Invalid email address' }),
  location: z.enum(['phone', 'meet'], {
    errorMap: () => ({ message: 'Please select a meeting location' }),
  }),
  topic: z.string().optional(),
  start: z.string().datetime(),
  end: z.string().datetime(),
  duration: z.string(),
  timeZone: z.string().min(1),
  oneOnOneOrderId: z.string().optional(),
});

type BookingFormInputs = z.infer<typeof bookingSchema>;

const locations = [
  {
    name: 'Google Meet',
    value: 'meet',
  },
];

export default function BookingForm({ title, price }: DebugMediaItem) {
  const [user, setUser] = useState({ name: '', email: '' });
  const { modal, selectedTime, timeZone, duration, setModal, setSelectedItem, selectedItem } =
    useOneOnOneStore();
  const { fetchNotification } = useNotificationStore();
  const { handleOneOnOnePayment, oneOnOnepaymentId } = usePayment();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BookingFormInputs>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      location: 'meet',
    },
  });

  useEffect(() => {
    const getProfileInfo = async () => {
      const data: any = await getProfile();
      const fullName = `${data.firstName} ${data.lastName}`.trim();

      setUser({
        name: fullName,
        email: data.email,
      });

      setValue('name', fullName);
      setValue('email', data.email);

      if (selectedTime) {
        setValue('start', selectedTime.start.toISOString());
        setValue('end', selectedTime.end.toISOString());
        setValue('duration', duration.toString());
        setValue('timeZone', timeZone || '');
        setValue('topic', title);
      }
    };

    getProfileInfo();
  }, [selectedTime, duration, timeZone, title, setValue]);

  if (!selectedTime || !timeZone) {
    return <></>;
  }

  const dateString = formatLocalDate(selectedTime.start, { timeZone });
  const startString = formatLocalTime(selectedTime.start, { timeZone });
  const endString = formatLocalTime(selectedTime.end, {
    timeZone,
    timeZoneName: 'shortGeneric',
  });

  const onSubmit: SubmitHandler<BookingFormInputs> = async formData => {
    try {
      setModal('busy');

      // Perform payment
      const payment = await handleOneOnOnePayment({
        id: selectedItem.id,
        amount: price,
        description: `1:1 Session on topic: ${title}`,
        topic: title,
        date: selectedTime?.start || new Date(),
        via: 'gmeet',
        link: '',
        start: formData?.start,
        end: formData?.end,

        email: formData.email,
        name: formData.name,
        location: 'meet',
        duration: formData.duration,
        timeZone: formData.timeZone,
      });

      if (!payment) {
        throw new Error('Payment failed or was canceled.');
      }

      formData.oneOnOneOrderId = payment.newOrder.id;

      const response = await fetch(`/api/one-on-one/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const json = await response.json();

      if (json.success) {
        fetchNotification();
        toast({
          title: `Appointment Request Sent`,
          description: `Thank you for your request! I'll be in touch shortly with further details.`,
          icon: doneLottie,
        });
      } else {
        throw new Error('Failed to send the appointment request.');
      }
    } catch (error: any) {
      console.error('Error during submission:', error);
      setModal('error');
      toast({
        title: `Error`,
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setModal('closed');
      setSelectedItem(null);
    }
  };

  if (oneOnOnepaymentId) {
    return <PollingUi />;
  }

  return (
    modal !== 'closed' && (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='mt-3 sm:mt-0 sm:ml-4 !text-brand-primary-text p-4 pt-0'
      >
        <Script src='https://checkout.razorpay.com/v1/checkout.js' strategy='lazyOnload' />
        <div className='text-base font-semibold leading-6 text-brand-primary-text'>
          Confirm session
        </div>

        <div className='border-l-4 border-l-brand-primary bg-slate-800 p-3 mt-3 mb-4 rounded-md'>
          <p className='text-sm md:text-base font-semibold text-accent-800'>{dateString}</p>
          <p className='text-xs md:text-sm'>
            {startString} - {endString}
          </p>
        </div>

        <div className='flex flex-col space-y-4'>
          <div className='isolate -space-y-px rounded-md shadow-sm'>
            <div className='relative rounded-md rounded-b-none pt-2.5 pb-1.5'>
              <DMInput
                label='Name'
                aria-label='Name'
                type='text'
                inputMode='text'
                {...register('name')}
                autoCapitalize='words'
                autoComplete='name'
                value={user.name}
                onChange={e => {
                  setUser(prev => ({ ...prev, name: e.target.value }));
                  setValue('name', e.target.value);
                }}
                errorMessage={errors.name as ErrorMessage}
                inputClass='w-full !border !border-brand-primary-border !bg-slate-800'
                placeholder='Jane Smith'
              />
            </div>
            <div className='relative rounded-md rounded-t-none pt-2.5 pb-1.5'>
              <DMInput
                label='Email'
                {...register('email')}
                value={user.email}
                onChange={e => {
                  setUser(prev => ({ ...prev, email: e.target.value }));
                  setValue('email', e.target.value);
                }}
                disabled
                aria-label='Email'
                inputMode='email'
                type='email'
                errorMessage={errors.email as ErrorMessage}
                inputClass='w-full !border !border-brand-primary-border !bg-slate-800'
                placeholder='jsmith@gmail.com'
              />
            </div>
          </div>

          <div>
            <p className='text-sm font-medium'>How would you like to meet?</p>
            <fieldset className='mt-2'>
              <div className='space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-4'>
                {locations.map(location => (
                  <div key={location.value} className='flex items-center'>
                    <input
                      id={location.value}
                      aria-label={location.name}
                      type='radio'
                      value={location.value}
                      {...register('location')}
                      defaultChecked={location.value === locations[0].value}
                      className='h-4 w-4 border-gray-300 text-brand-primary-text focus:ring-accent-600'
                    />
                    <label
                      htmlFor={location.value}
                      className='ml-1.5 block text-sm leading-6 text-brand-primary-text'
                    >
                      {location.name}
                    </label>
                  </div>
                ))}
                {errors.location && (
                  <p className='text-red-500 text-xs mt-1'>{errors.location.message}</p>
                )}
              </div>
            </fieldset>
          </div>
        </div>

        {modal === 'error' && (
          <div className='bg-red-50 text-red-600'>There was an error submitting your request.</div>
        )}

        <div className='mt-5 sm:mt-4 sm:flex sm:flex-row-reverse'>
          <DMButton
            variant='primary'
            type='submit'
            disabled={isSubmitting}
            className='inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto disabled:opacity-50'
          >
            {isSubmitting ? <Loader className='animate-spin' /> : 'Book Now'}
          </DMButton>
          <DMButton
            variant='secondary'
            type='button'
            className='mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-slate-400 shadow-sm ring-1 ring-inset sm:mt-0 sm:w-auto'
            onClick={() => {
              setModal('closed');
            }}
          >
            Cancel
          </DMButton>
        </div>
      </form>
    )
  );
}
